import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role ?? null;

  const perms = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  );

  return useMemo(
    () => {
      // Permissions exist in two schemes that run side by side (see Flyway
      // V122 dual-write): legacy hand-crafted codes like `CREATE_SRF`, and
      // catalogue-generated codes `${MODULE}_${ACTION}` like `SRF_CREATE`.
      // Granting a permission through the no-code Role/Module catalogue issues
      // ONLY the new code, so every gate must accept both forms or the button
      // stays hidden for catalogue-granted users.
      const hasAny = (...codes) => codes.some((code) => perms.has(code));

      return {
        role,
        permissions: perms,
        has: (code) => perms.has(code),
        /**
         * Check a module's screen-access permission.
         * Tries new-style `${MODULE}_ACCESS` first, then legacy `ACCESS_${PLURAL}` fallback.
         * Both forms are checked so the sidebar keeps working regardless of which
         * permission scheme the backend has been migrated to.
         *
         * @param {string} moduleCode  e.g. 'CUSTOMER'
         * @param {string} [legacyCode] e.g. 'ACCESS_CUSTOMERS'
         * @returns {boolean}
         */
        hasModuleAccess: (moduleCode, legacyCode) => {
          if (perms.has(`${moduleCode}_ACCESS`)) return true;
          if (legacyCode && perms.has(legacyCode)) return true;
          return false;
        },
        isAdmin: role === 'ADMIN',
        isCEO: role === 'CEO',
        // Legacy booleans preserved for backward compat. Each gate accepts both
        // the legacy code and its catalogue-generated equivalent.
        canManageTestCatalog:
          perms.has('MANAGE_TEST_CATALOG') || role === 'ADMIN' || role === 'CEO',
        canCreate:
          hasAny(
            'CREATE_CUSTOMER', 'CUSTOMER_CREATE',
            'CREATE_SRF', 'SRF_CREATE',
          ) ||
          role === 'ADMIN' ||
          role === 'CEO',
        canEdit:
          hasAny('EDIT_QUOTATION', 'QUOTATION_UPDATE') ||
          role === 'ADMIN' ||
          role === 'CEO',
        canDelete:
          hasAny('DELETE_CUSTOMER', 'CUSTOMER_DELETE') ||
          role === 'ADMIN' ||
          role === 'CEO',
        canApprove:
          hasAny(
            'APPROVE_QUOTATION', 'QUOTATION_APPROVE',
            'APPROVE_SRF', 'SRF_APPROVE',
            'APPROVE_USERS', 'ADMIN_APPROVE_USERS',
          ) ||
          role === 'ADMIN' ||
          role === 'CEO',
        canViewFinancials:
          perms.has('VIEW_FINANCIALS') || role === 'ADMIN' || role === 'CEO',
      };
    },
    [perms, role],
  );
}
